import { ClockIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import useProductUpdateInputModal from 'src/features/product/product-detail/hooks/useProductUpdateInputModal';
import AddressSelectionModal from 'src/features/product/product-detail/ui/quotes-tab/AddressSelectionModal';
import { CountryFlag } from 'src/features/product/product-detail/ui/quotes-tab/CountryFlag';
import isFileImage from 'src/features/product/product-detail/utils/isFileImage';
import {
  AppImage,
  ExpectedTag,
  FilesTab,
  UserAvatar
} from 'src/shared/components';
import {
  CTA_BULK_ORDERING,
  CTA_REQUEST_NEW_SAMPLE,
  CTA_SUPPORT
} from 'src/shared/constants/update';
import { Sampling } from 'src/shared/types/sampling';

import ImageGallery from 'src/shared/components/ImageGallery';
import {
  Chip,
  Divider,
  HeroModal as Modal,
  ModalContent,
  Tab,
  useDisclosure
} from 'src/shared/components/ui';
import { ButtonV2, Link, TabsV2, Typography } from 'src/shared/components/ui';

import getCompletedDate from '../../utils/getCompletedDate';
import getShipmentProductPhotos from '../../utils/getShipmentProductPhotos';
import SampleStage from '../sample-stage/SampleStage';
import SamplingEditModal from '../sampling-edit-modal/SamplingEditModal';
import SamplingModalDetails from './SamplingModalDetails';
import SamplingModalTimeline from './SamplingModalTimeline';

const MAX_TRACKING_URL_LENGTH = 30;

interface SamplingModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  sampling: Sampling | null;
  setSelectedSampling: (sampling: Sampling) => void;
}

const LabelValueRow = ({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) => {
  if (value === null) {
    return null;
  }

  return (
    <div className="flex items-center gap-5">
      <Typography
        variant="body-1"
        weight="medium"
        className="text-secondary-text min-w-20 break-all"
      >
        {label}
      </Typography>
      {value}
    </div>
  );
};

const SamplingModal: React.FC<SamplingModalProps> = ({
  isOpen,
  onOpenChange,
  sampling,
  setSelectedSampling
}) => {
  const [sampleTab, setSampleTab] = useState<string>('timeline');
  const samplingName = sampling?.name || '';

  const { isOpen: isEditOpen, onOpenChange: onEditOpenChange } =
    useDisclosure();

  const { setData: setUpdateInputData } = useProductUpdateInputModal();
  const {
    isOpen: isAddressOpen,
    onOpen: openAddress,
    onOpenChange: onAddressOpenChange,
    onClose: closeAddress
  } = useDisclosure();
  const [pendingBulkData, setPendingBulkData] = useState<{
    name: string;
    quantity?: number | null;
  } | null>(null);

  // The sample's own photos, then the shipment's files marked as product photos.
  const images = useMemo(
    () =>
      [
        ...(sampling?.uploaded_files?.filter(isFileImage) ?? []),
        ...getShipmentProductPhotos(
          sampling?.latest_user_shipping?.external_files
        )
      ].map((file) => ({
        url: file.url,
        description: file.description,
        name: file.name
      })),
    [sampling]
  );

  // The sample's own files, then those of the shipment that carried it.
  const files = useMemo(
    () => [
      ...(sampling?.uploaded_files ?? []),
      ...(sampling?.latest_user_shipping?.external_files ?? [])
    ],
    [sampling?.uploaded_files, sampling?.latest_user_shipping]
  );

  const visibleFiles = useMemo(() => {
    return files.filter((file) => !file.tags?.includes('visibility:hidden'));
  }, [files]);

  const hasFiles = visibleFiles.length > 0;

  const hasDetails = !!(
    sampling?.product_quote ||
    sampling?.description ||
    sampling?.customer_notes ||
    sampling?.uploaded_files?.some((f) => f.tags?.includes('qa-report'))
  );

  useEffect(() => {
    if (!hasDetails && sampleTab === 'details') {
      setSampleTab('timeline');
    }
  }, [hasDetails, sampleTab]);

  const renderTabContent = () => {
    switch (sampleTab) {
      case 'timeline':
        return <SamplingModalTimeline sampling={sampling} isOpen={isOpen} />;

      case 'details':
        return <SamplingModalDetails sampling={sampling} />;

      case 'files':
        return <FilesTab files={files} />;

      default:
        return '';
    }
  };

  const invoiceFiles = useMemo(() => {
    if (sampling?.uploaded_files) {
      return sampling?.uploaded_files?.filter((file) =>
        file.tags?.includes('invoice')
      );
    }

    return [];
  }, [sampling]);

  const renderInvoiceFiles = () => {
    if (invoiceFiles?.length > 0) {
      return (
        <LabelValueRow
          label="Invoice"
          value={
            <div className="flex gap-2 flex-wrap">
              {invoiceFiles?.map((file) => (
                <Link key={file?.url ?? file?.name} isExternal to={file?.url}>
                  <span className="break-all">{file?.name}</span>
                </Link>
              ))}
            </div>
          }
        />
      );
    }

    return null;
  };

  return (
    <>
      <Modal
        hideCloseButton
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        className="h-[90%] max-w-[960px]"
        id="sampling-modal"
      >
        <ModalContent className="overflow-y-auto">
          <div className="flex flex-col p-5">
            <div className="md:grid flex flex-col-reverse md:grid-cols-[1.2fr_1fr] gap-6">
              <div className="flex flex-col">
                {images.length > 0 ? (
                  <ImageGallery
                    title={sampling?.name}
                    imageUrls={images}
                    isCarousel
                  />
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="relative w-full h-[440px] bg-gray-2 rounded-2xl flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center z-20 gap-1">
                        <ClockIcon className="size-7 text-white" />
                        <Typography
                          variant="body-1"
                          weight="medium"
                          className="text-white"
                        >
                          Image pending
                        </Typography>
                      </div>
                      <AppImage id={sampling?.id} alt="No image" />
                    </div>
                    <div className="min-w-14 min-h-14 max-w-14 max-h-14 bg-gray-2 rounded-2xl flex items-center justify-center">
                      <AppImage
                        id={sampling?.id}
                        alt="No image"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="flex flex-col gap-1">
                    <Chip color="default" className="bg-gray-2 rounded-lg">
                      <Typography
                        variant="body-3"
                        weight="medium"
                        className="text-secondary-text"
                      >
                        Name: {sampling?.name}
                      </Typography>
                    </Chip>

                    <div
                      onClick={onEditOpenChange}
                      className="group flex items-center gap-2 cursor-pointer"
                    >
                      <Typography variant="h4" weight="bold">
                        {sampling?.name}
                      </Typography>
                      <ButtonV2
                        size="sm"
                        isIconOnly
                        variant="flat"
                        color="default"
                        className="group-hover:opacity-100 opacity-0 group-hover:flex hidden p-1 rounded-lg"
                        onPress={onEditOpenChange}
                        startContent={
                          <PencilIcon className="size-4 pointer-events-none" />
                        }
                      />
                    </div>
                  </div>
                  {sampling?.product_quote?.sample_total_cost_marked_up && (
                    <div className="flex flex-col gap-1 sm:items-end sm:text-right">
                      <Typography
                        variant="body-2"
                        weight="medium"
                        className="text-secondary-text"
                      >
                        Total sample cost
                      </Typography>
                      <Typography variant="h4" weight="bold">
                        ${sampling.product_quote.sample_total_cost_marked_up}
                      </Typography>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {sampling?.product_quote?.product_name && (
                    <LabelValueRow
                      label="Type"
                      value={sampling?.product_quote?.product_name}
                    />
                  )}
                  <LabelValueRow
                    label="Supplier"
                    value={
                      <div className="flex items-center gap-1">
                        <span>{sampling?.cloak}</span>
                        {sampling?.product_quote?.quote?.supplier_factory
                          ?.country_code && (
                          <CountryFlag
                            countryCode={
                              sampling?.product_quote?.quote?.supplier_factory
                                ?.country_code
                            }
                          />
                        )}
                      </div>
                    }
                  />
                  <LabelValueRow
                    label="Buyer"
                    value={
                      sampling?.user ? (
                        <div className="flex items-center gap-1">
                          <UserAvatar
                            name={sampling?.user?.username}
                            image={sampling?.user?.avatar_url ?? undefined}
                            size="xs"
                          />
                          <span>{sampling?.user?.username}</span>
                        </div>
                      ) : null
                    }
                  />
                  <LabelValueRow
                    label="Stage"
                    value={<SampleStage stage={sampling?.stage ?? ''} />}
                  />
                  {sampling?.requested_at && (
                    <LabelValueRow
                      label="Requested"
                      value={format(sampling?.requested_at, 'MMM dd, yyyy')}
                    />
                  )}
                  {sampling?.estimated_end_date &&
                    (() => {
                      const completedDate = getCompletedDate(sampling);

                      return (
                        <LabelValueRow
                          label="Completion"
                          value={
                            <div className="flex items-center gap-1">
                              {completedDate.date}
                              {completedDate.is_expected && <ExpectedTag />}
                            </div>
                          }
                        />
                      );
                    })()}
                </div>
                {sampling?.latest_user_shipping?.tracking_url && (
                  <LabelValueRow
                    label="Tracking"
                    value={
                      <Link
                        isExternal
                        to={sampling.latest_user_shipping.tracking_url}
                      >
                        {sampling.latest_user_shipping.tracking_url.length >
                        MAX_TRACKING_URL_LENGTH
                          ? `${sampling.latest_user_shipping.tracking_url.substring(0, MAX_TRACKING_URL_LENGTH)}...`
                          : sampling.latest_user_shipping.tracking_url}
                      </Link>
                    }
                  />
                )}
                {renderInvoiceFiles()}

                <div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <ButtonV2
                      onPress={() => {
                        if (sampling) {
                          setPendingBulkData({
                            name: samplingName,
                            quantity: sampling?.product_quote?.quote?.quantity
                          });
                          openAddress();
                        }
                      }}
                    >
                      Bulk order
                    </ButtonV2>
                    <ButtonV2
                      onPress={() => {
                        if (sampling) {
                          const data = {
                            name: samplingName,
                            supplierCloak: sampling.cloak,
                            productQuoteId:
                              sampling.product_quote?.id.toString()
                          };

                          setUpdateInputData(data, CTA_REQUEST_NEW_SAMPLE);
                        }
                      }}
                      color="secondary"
                    >
                      Request new sample
                    </ButtonV2>

                    <ButtonV2
                      onPress={() => {
                        if (sampling) {
                          const data = {
                            name: samplingName,
                            supplierCloak: sampling.cloak,
                            productQuoteId:
                              sampling.product_quote?.id.toString()
                          };

                          setUpdateInputData(data, CTA_SUPPORT);
                        }
                      }}
                      color="secondary"
                    >
                      Support
                    </ButtonV2>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="px-3">
              <TabsV2
                selectedKey={sampleTab}
                onSelectionChange={(key) => {
                  setSampleTab(key as string);
                }}
              >
                <Tab key="timeline" title="Timeline" />
                {hasDetails && <Tab key="details" title="Sample details" />}
                {hasFiles && <Tab key="files" title="Files" />}
              </TabsV2>
            </div>
            <Divider className="w-full h-[1px] m-0 relative top-[-4px]" />

            <div>{renderTabContent()}</div>

            <SamplingEditModal
              isOpen={isEditOpen}
              onOpenChange={onEditOpenChange}
              sampling={sampling as Sampling}
              setSelectedSampling={setSelectedSampling}
            />
          </div>
        </ModalContent>
      </Modal>

      <AddressSelectionModal
        isOpen={isAddressOpen}
        onOpenChange={onAddressOpenChange}
        onConfirm={(address) => {
          closeAddress();
          if (pendingBulkData) {
            setUpdateInputData(
              { ...pendingBulkData, address },
              CTA_BULK_ORDERING
            );
            setPendingBulkData(null);
          }
        }}
        title="Select shipping address"
        confirmLabel="Continue"
      />
    </>
  );
};

export default SamplingModal;
